import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { useBusinessByAbnOrAcn, useSearchBusinessByName } from '../api/field';
import { Nullable } from '../types';
import { Business } from '../types/models';
import { BusinessSearchResult } from '../types/requests';

type UseBusinessLookup = {
  businessSearchResults: BusinessSearchResult[];
  isSearching: boolean;
  selectedBusiness: Nullable<Business>;
  setIsSearching: Dispatch<SetStateAction<boolean>>;
  setSelectedBusiness: Dispatch<SetStateAction<Nullable<Business>>>;
};

const abnOrAcnMaxLength = 11;

const validateSearchInput = {
  byAbnOrAcn: (value: string) =>
    /^[0-9]+$/.test(value) && value.length <= abnOrAcnMaxLength,
  byName: (value: string) => /^[a-zA-Z\s]+$/.test(value),
};

export function useBusinessLookup(inputValue: string): UseBusinessLookup {
  const { searchBusinessByName } = useSearchBusinessByName();
  const { getBusinessByAbnOrAcn } = useBusinessByAbnOrAcn();
  const [businessSearchResults, setBusinessSearchResults] = useState<
    BusinessSearchResult[]
  >([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchableValue, setSearchableValue] = useState<string>('');
  const [selectedBusiness, setSelectedBusiness] =
    useState<Nullable<Business>>(null);

  const onInputValue = (): void => {
    const value = /^(\d|\s)+$/.test(inputValue)
      ? inputValue.replace(/\s/g, '')
      : inputValue;

    if (businessSearchResults.length > 0) {
      setBusinessSearchResults([]);
    }

    if (selectedBusiness) {
      setSelectedBusiness(null);
    }

    if (
      Object.values(validateSearchInput).some((validatorFunc) =>
        validatorFunc(value),
      )
    ) {
      setIsSearching(true);
      setSearchableValue(value);
    } else {
      // @TODO Handle invalid input
    }
  };

  const performSearch = async (): Promise<void> => {
    try {
      if (validateSearchInput.byName(searchableValue)) {
        const response = await searchBusinessByName(searchableValue);
        setBusinessSearchResults(response?.data ?? []);
      } else if (validateSearchInput.byAbnOrAcn(searchableValue)) {
        const response = await getBusinessByAbnOrAcn(searchableValue);
        setSelectedBusiness(response?.data ?? null);
      }
    } finally {
      setIsSearching(false);
    }
  };

  const debouncedPerformSearch = useDebouncedCallback(performSearch, 500);

  useEffect(() => {
    debouncedPerformSearch();
  }, [searchableValue]);

  useEffect(() => {
    onInputValue();
  }, [inputValue]);

  return {
    businessSearchResults,
    isSearching,
    selectedBusiness,
    setIsSearching,
    setSelectedBusiness,
  };
}
